import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TableStatesComponent } from '@components/table-states/table-states.component';
import { StatusPillComponent } from '@components/status-pill/status-pill.component';
import { TimeAgoPipe } from '@common/pipes/time-ago.pipe';
import { formatMileageAlarmReading, formatSpeedAlarmReading } from '@common/index';
import { formatDateTime } from '@common/utils/format-time';
import {
  GetNotificationsParams,
  Notification,
  NotificationSeverity,
  NotificationService,
} from '@services/notification.service';
import { RealtimeService } from '@services/realtime.service';
import { Pagination } from '@interfaces/index';
import { PillTone } from '@components/status-pill/interfaces/status-pill.interface';
import {
  NotificationView,
  NotificationRouteTarget,
} from '@components/notification-panel/interfaces/notification-panel.interface';

const PER_PAGE = 20;
/** Scrolled further down than this, a new notification waits behind a pill instead of moving the list. */
const SCROLLED_PX = 40;
/** How long a just-arrived row keeps its highlight. */
const FRESH_MS = 3_200;

const SEVERITY_TONE: Record<NotificationSeverity, PillTone> = {
  info: 'info',
  warning: 'warn',
  critical: 'crit',
};

/**
 * One renderer per known `type`. Anything not listed here falls back to a
 * generic row (type + severity + timestamp only) instead of breaking —
 * more types get a real renderer added here as their producers ship.
 */
const TYPE_RENDERERS: Partial<Record<string, (n: Notification) => NotificationView>> = {
  kyc_review_pending: (n) => ({
    title: 'KYC review pending',
    detail: `${n.payload.driverName ?? 'Unknown driver'} — ${n.payload.driverEmail ?? ''}`,
  }),
  speed_limit_exceeded: (n) => ({
    title: 'Speed limit exceeded',
    detail: `${n.payload.plate ?? 'Unknown vehicle'}${n.payload.driverName ? ' · ' + n.payload.driverName : ''} — ${formatSpeedAlarmReading(n.payload)} in ${n.payload.zoneName ?? 'a speed zone'}`,
  }),
  vehicle_blocked: (n) => {
    const cancelled = n.payload.cancelledBookings ?? 0;
    return {
      title: `${n.payload.plate ?? 'A vehicle'} taken off the road`,
      detail: `${n.payload.title ?? 'A report'} · ${
        cancelled
          ? `${cancelled} booking${cancelled === 1 ? '' : 's'} cancelled`
          : 'no bookings affected'
      }`,
    };
  },
  maintenance_overdue: (n) => ({
    title: `Maintenance overdue · ${n.payload.plate ?? 'a vehicle'}`,
    detail: `${n.payload.title ?? 'The job'} was due ${formatDateTime(n.payload.endAt)}. The vehicle is still blocked until the job is completed.`,
  }),
  mileage_threshold_exceeded: (n) => ({
    title: `Service due · ${n.payload.plate ?? 'a vehicle'}`,
    detail: `${formatMileageAlarmReading(n.payload)} — the vehicle is blocked for maintenance until the job is completed.`,
  }),
  out_of_zone: (n) => ({
    title: 'Out of zone',
    detail: `${n.payload.plate ?? 'Unknown vehicle'}${n.payload.driverName ? ' · ' + n.payload.driverName : ''} — left ${n.payload.zoneName ?? 'its pickup zone'}`,
  }),
};

/**
 * One route per known `entityType` a click can deep-link to. Filters by
 * whatever's already visible on the destination screen (here, the KYC
 * queue's own free-text search) rather than an invisible id param, so the
 * active filter is self-evident once you land. No entry → click only marks
 * read, same as an unrecognised notification type.
 */
const ENTITY_ROUTES: Partial<Record<string, (n: Notification) => NotificationRouteTarget>> = {
  kyc_status: (n) => ({
    commands: ['/users'],
    queryParams: { search: n.payload.driverEmail ?? '' },
  }),
  // Fleet alarms (speed_alarm today, zone_alarm / late_return_alarm later)
  // all land on the command dashboard's Active Alarms panel, which selects
  // and scrolls to the matching alarm by its notification id.
  speed_alarm: (n) => ({
    commands: ['/dashboard'],
    queryParams: { alarmId: n.id },
  }),
  zone_alarm: (n) => ({
    commands: ['/dashboard'],
    queryParams: { alarmId: n.id },
  }),
  // A block and its overdue alarm both land on the job they are about.
  maintenance_job: (n) => ({
    commands: ['/maintenance'],
    queryParams: { id: n.entityId ?? '' },
  }),
  maintenance_overdue: (n) => ({
    commands: ['/maintenance'],
    queryParams: { id: n.entityId ?? '' },
  }),
  // A mileage alarm is about a listing, but the useful destination is the
  // block it opened.
  listing: (n) => ({
    commands: ['/maintenance'],
    queryParams: { id: n.payload.maintenanceJobId ?? '' },
  }),
};

@Component({
  selector: 'app-notification-panel',
  imports: [
    NzSpinModule,
    NzButtonModule,
    NzIconModule,
    TableStatesComponent,
    StatusPillComponent,
    TimeAgoPipe,
  ],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss',
})
export class NotificationPanelComponent {
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  protected realtime = inject(RealtimeService);
  private destroyRef = inject(DestroyRef);
  private listEl = viewChild<ElementRef<HTMLElement>>('listEl');

  /** Whether the dropdown is currently open — gates fetching. */
  isOpen = input.required<boolean>();
  /** Emitted after a successful mark-read/mark-all-read, so the bell badge can refresh immediately. */
  readonly unreadChanged = output<void>();
  /** Emitted after navigating away from a row click, so the dropdown closes behind it. */
  readonly closed = output<void>();

  private page = signal(1);
  private accumulated = signal<Notification[]>([]);
  private total = signal(0);
  private markingIds = signal<ReadonlySet<string>>(new Set());
  /** Rows that arrived live a moment ago; they carry a fading highlight. */
  private freshIds = signal<ReadonlySet<string>>(new Set());
  /** New notifications that arrived while the list was scrolled down. */
  protected pending = signal<Notification[]>([]);
  private scrolledDown = signal(false);

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.page.set(1);
        this.accumulated.set([]);
        this.total.set(0);
        this.pending.set([]);
        this.scrolledDown.set(false);
      }
    });

    // Live: the server says something changed (or events may have been missed),
    // so the first page is re-read and merged. Only while the panel is open;
    // a closed panel loads fresh next time anyway.
    this.realtime.notificationsChanged$.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (!this.isOpen()) {
        return;
      }
      // A resolved alarm is marked on the row we already have, even one further
      // down than the first page; the re-read then confirms it.
      if (event?.action === 'resolved') {
        this.markResolved(event.data['id'], event.at);
      }
      this.syncFirstPage();
    });

    // page 1 (the first load) replaces the list; any later page
    // (scrolled-to "load more") appends instead, deduped by id.
    effect(() => {
      const value = this.notificationsResource.value();
      if (!value) {
        return;
      }
      this.total.set(value.total);
      if (value.page === 1) {
        this.accumulated.set(value.results);
      } else {
        this.accumulated.update((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          return [...prev, ...value.results.filter((n) => !seen.has(n.id))];
        });
      }
    });
  }

  private queryParams = computed<GetNotificationsParams | null>(() => {
    if (!this.isOpen()) {
      return null;
    }
    return { page: this.page(), perPage: PER_PAGE };
  });

  notificationsResource = rxResource<Pagination<Notification>, GetNotificationsParams | null>({
    params: () => this.queryParams(),
    stream: ({ params }) => {
      if (!params) {
        return of({ page: 1, perPage: PER_PAGE, total: 0, results: [] as Notification[] });
      }
      return this.notificationService.getAll(params);
    },
  });

  isLoading = computed(() => this.notificationsResource.isLoading());
  /** Only true for the very first fetch — the big spinner shouldn't cover
   *  the whole list again for a "load more" append. */
  isInitialLoading = computed(() => this.isLoading() && this.page() === 1);
  isLoadingMore = computed(() => this.isLoading() && this.page() > 1);
  notifications = computed(() => this.accumulated());

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.scrolledDown.set(el.scrollTop > SCROLLED_PX);
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      this.loadMore();
    }
  }

  /**
   * Merges the newest page into what is already shown: rows already here pick up
   * their new read/resolved state in place; rows newer than everything here are
   * new arrivals. If the person has scrolled down they wait behind a pill, so the
   * list never moves under what they are reading.
   */
  private syncFirstPage(): void {
    this.notificationService.getAll({ page: 1, perPage: PER_PAGE }).subscribe((first) => {
      if (!this.isOpen() || first.results.length === 0) {
        return;
      }
      const current = this.accumulated();
      const byId = new Map(first.results.map((n) => [n.id, n]));
      const newest = current[0] ? new Date(current[0].createdAt).getTime() : 0;
      const known = new Set([...current, ...this.pending()].map((n) => n.id));
      const arrivals = first.results.filter(
        (n) => !known.has(n.id) && new Date(n.createdAt).getTime() > newest,
      );

      this.accumulated.set(
        current.map((n) => {
          const latest = byId.get(n.id);
          return latest ? { ...n, isRead: latest.isRead, resolvedAt: latest.resolvedAt } : n;
        }),
      );
      this.total.set(first.total);

      if (arrivals.length === 0) {
        return;
      }
      if (this.scrolledDown()) {
        this.pending.update((p) => [...arrivals, ...p]);
      } else {
        this.prepend(arrivals);
      }
    });
  }

  private markResolved(id: string | null | undefined, at: string): void {
    if (!id) {
      return;
    }
    this.accumulated.update((rows) =>
      rows.map((n) => (n.id === id && !n.resolvedAt ? { ...n, resolvedAt: at } : n)),
    );
  }

  protected showPending(): void {
    this.prepend(this.pending());
    this.pending.set([]);
    this.listEl()?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private prepend(arrivals: Notification[]): void {
    this.accumulated.update((prev) => [...arrivals, ...prev]);
    const ids = arrivals.map((n) => n.id);
    this.freshIds.update((fresh) => new Set([...fresh, ...ids]));
    const timer = setTimeout(
      () =>
        this.freshIds.update((fresh) => {
          const next = new Set(fresh);
          ids.forEach((id) => next.delete(id));
          return next;
        }),
      FRESH_MS,
    );
    this.destroyRef.onDestroy(() => clearTimeout(timer));
  }

  protected isFresh(id: string): boolean {
    return this.freshIds().has(id);
  }

  private loadMore(): void {
    if (this.isLoading() || this.accumulated().length >= this.total()) {
      return;
    }
    this.page.update((p) => p + 1);
  }

  view(notification: Notification): NotificationView {
    const renderer = TYPE_RENDERERS[notification.type];
    return (
      renderer?.(notification) ?? {
        title: notification.type,
        detail: 'No details available for this notification type yet',
      }
    );
  }

  tone(notification: Notification): PillTone {
    return SEVERITY_TONE[notification.severity] ?? 'neutral';
  }

  isResolved(notification: Notification): boolean {
    return notification.actionRequired && notification.resolvedAt !== null;
  }

  isMarking(id: string): boolean {
    return this.markingIds().has(id);
  }

  isNavigable(notification: Notification): boolean {
    return !!notification.entityType && !!ENTITY_ROUTES[notification.entityType];
  }

  onRowClick(notification: Notification): void {
    if (!notification.isRead && !this.isMarking(notification.id)) {
      this.setMarking(notification.id, true);
      this.notificationService.markRead(notification.id).subscribe((ok) => {
        this.setMarking(notification.id, false);
        if (ok) {
          this.applyRead([notification.id]);
          this.unreadChanged.emit();
        }
      });
    }

    const route = notification.entityType ? ENTITY_ROUTES[notification.entityType] : undefined;
    if (route) {
      const { commands, queryParams } = route(notification);
      void this.router.navigate(commands, { queryParams });
      this.closed.emit();
    }
  }

  markAllRead(): void {
    const unreadIds = this.notifications()
      .filter((n) => !n.isRead)
      .map((n) => n.id);
    if (!unreadIds.length) {
      return;
    }
    this.notificationService.markAllRead().subscribe((ok) => {
      if (ok) {
        this.applyRead(unreadIds);
        this.unreadChanged.emit();
      }
    });
  }

  private setMarking(id: string, marking: boolean): void {
    const next = new Set(this.markingIds());
    if (marking) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.markingIds.set(next);
  }

  private applyRead(ids: string[]): void {
    const idSet = new Set(ids);
    this.accumulated.update((results) =>
      results.map((n) => (idSet.has(n.id) ? { ...n, isRead: true } : n)),
    );
  }
}
